import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Collapse,
  Heading,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  csvConversion,
  utils as commonUtils,
} from '@finley-technologies/common';
import { progress } from '@finley-technologies/tape-builder';
import { ColumnInfo } from '@finley-technologies/tape-sanitizer';

import { colors } from 'packages/common-frontend/colors';

// Fragments
type FieldNameCellProps = { fieldInfo: ColumnInfo };
const FieldNameCell = ({ fieldInfo }: FieldNameCellProps) => (
  <Td>
    {fieldInfo.name}
    {(() => {
      const optional = !fieldInfo.required;
      if (optional && fieldInfo.defaultValue) {
        return (
          <Text fontSize="xs">
            (optional, defaults to {fieldInfo.defaultValue})
          </Text>
        );
      }
      if (optional) {
        return <Text fontSize="xs">(optional)</Text>;
      }
      return <></>;
    })()}
  </Td>
);

type ScrollableTableProps = {
  children: React.ReactNode;
  numOfRows: number;
};
const ScrollableTable = ({ children, numOfRows }: ScrollableTableProps) => {
  const maxHeight = numOfRows * 45;
  return (
    <Box overflowY="auto" maxHeight={`${maxHeight}px`}>
      {children}
    </Box>
  );
};

const MessageErrorView = ({ error }: { error: progress.MessageError }) => (
  <Alert status="error" mb="24px" borderRadius="6px">
    <AlertIcon />
    <AlertDescription>{error.message}</AlertDescription>
  </Alert>
);

const DataValidationErrorView = ({
  error,
  numOfRows,
  collapsible,
}: {
  error: progress.DataValidationError;
  numOfRows: number;
  collapsible: boolean;
}) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: !collapsible });
  return (
    <Box>
      <Alert status="error" mb={isOpen ? '24px' : '0px'} borderRadius="6px">
        <AlertIcon />
        <AlertDescription>
          {`There are invalid ${commonUtils.toTitleCase(
            error.dataTapeSlug,
          )} fields.`}
          {collapsible && (
            <Button
              size="sm"
              color="blue.300"
              variant="link"
              onClick={onToggle}
              ml="8px"
              _focus={{ boxShadow: 'none' }}
            >
              {isOpen ? 'Hide Errors' : 'View Errors'}
            </Button>
          )}
        </AlertDescription>
      </Alert>
      <Collapse in={isOpen} animateOpacity>
        <Heading as="h5" size="sm" mb="24px">
          Invalid {commonUtils.toTitleCase(error.dataTapeSlug)} fields
        </Heading>
        {!!error.xlsxExtractionErrors.length && (
          <ScrollableTable numOfRows={numOfRows}>
            <Table mb="24px" border="1px" borderColor={colors.gray[100]}>
              <Thead>
                <Tr>
                  <Th>Sheet name</Th>
                  <Th>Cell</Th>
                  <Th>Field name</Th>
                  <Th>Error</Th>
                </Tr>
              </Thead>
              <Tbody fontSize="sm">
                {error.xlsxExtractionErrors.map(
                  ({ sheetName, cell, fieldName, message }) => (
                    <Tr key={`${sheetName},${cell},${fieldName}`}>
                      <Td>{sheetName}</Td>
                      <Td>{cell}</Td>
                      <Td>{fieldName}</Td>
                      <Td>{message}</Td>
                    </Tr>
                  ),
                )}
              </Tbody>
            </Table>
          </ScrollableTable>
        )}
        {!!error.xlsxCellErrors.length && (
          <ScrollableTable numOfRows={numOfRows}>
            <Table mb="24px" border="1px" borderColor={colors.gray[100]}>
              <Thead>
                <Tr>
                  <Th>Sheet name</Th>
                  <Th>
                    {error.xlsxCellErrors[0].cell === undefined
                      ? 'Row'
                      : 'Cell'}
                  </Th>
                  <Th>Field name</Th>
                  <Th>Entered value</Th>
                  <Th>Expected type</Th>
                  <Th>Examples</Th>
                </Tr>
              </Thead>
              <Tbody fontSize="sm">
                {error.xlsxCellErrors.map(
                  ({ sheetName, fieldInfo, cell, rowNumber, enteredValue }) => (
                    <Tr
                      key={`${sheetName},${cell},${rowNumber},${fieldInfo.name}`}
                    >
                      <Td>{sheetName}</Td>
                      <Td>{cell ?? rowNumber}</Td>
                      <FieldNameCell fieldInfo={fieldInfo} />
                      <Td>{enteredValue}</Td>
                      <Td>{fieldInfo.type}</Td>
                      <Td>{fieldInfo.examples.join(', ')}</Td>
                    </Tr>
                  ),
                )}
              </Tbody>
            </Table>
          </ScrollableTable>
        )}
        {!!error.xlsxColumnErrors.length && (
          <ScrollableTable numOfRows={numOfRows}>
            <Table mb="24px" border="1px" borderColor={colors.gray[100]}>
              <Thead>
                <Tr>
                  <Th>Sheet name</Th>
                  <Th>Field name</Th>
                  <Th>Expected type</Th>
                  <Th>Examples</Th>
                </Tr>
              </Thead>
              <Tbody fontSize="sm">
                {error.xlsxColumnErrors.map(({ sheetName, fieldInfo }) => (
                  <Tr key={`${sheetName},${fieldInfo.name}`}>
                    <Td>{sheetName}</Td>
                    <FieldNameCell fieldInfo={fieldInfo} />
                    <Td>{fieldInfo.type}</Td>
                    <Td>{fieldInfo.examples.join(', ')}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </ScrollableTable>
        )}
        {!!error.csvFieldErrors.length && (
          <ScrollableTable numOfRows={numOfRows}>
            <Table mb="24px" border="1px" borderColor={colors.gray[100]}>
              <Thead>
                <Tr>
                  <Th>Field name</Th>
                  <Th>Row</Th>
                  <Th>Entered value</Th>
                  <Th>Expected type</Th>
                  <Th>Examples</Th>
                </Tr>
              </Thead>
              <Tbody fontSize="sm">
                {error.csvFieldErrors.map(
                  ({ fieldInfo, rowNumber, enteredValue }) => (
                    <Tr key={`${rowNumber},${fieldInfo.name}`}>
                      <FieldNameCell fieldInfo={fieldInfo} />
                      <Td>{rowNumber}</Td>
                      <Td>{enteredValue}</Td>
                      <Td>{fieldInfo.type}</Td>
                      <Td>{fieldInfo.examples.join(', ')}</Td>
                    </Tr>
                  ),
                )}
              </Tbody>
            </Table>
          </ScrollableTable>
        )}
        {!!error.csvColumnErrors.length && (
          <ScrollableTable numOfRows={numOfRows}>
            <Table mb="24px" border="1px" borderColor={colors.gray[100]}>
              <Thead>
                <Tr>
                  <Th>Field name</Th>
                  <Th>Expected type</Th>
                  <Th>Examples</Th>
                </Tr>
              </Thead>
              <Tbody fontSize="sm">
                {error.csvColumnErrors.map(({ fieldInfo }) => (
                  <Tr key={fieldInfo.name}>
                    <FieldNameCell fieldInfo={fieldInfo} />
                    <Td>{fieldInfo.type}</Td>
                    <Td>{fieldInfo.examples.join(', ')}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </ScrollableTable>
        )}
      </Collapse>
    </Box>
  );
};

const StructuredPublicErrorView = ({
  error,
  numOfRows,
}: {
  error: progress.SerializedStructuredPublicError;
  numOfRows: number;
}) => {
  const { externalMessage, tableData } = error;
  const data = tableData
    ? csvConversion.csvStringToJsonArray({
        csvString: tableData.csvString,
      }).data
    : null;
  const omittedRows = tableData?.omittedRows ?? 0;
  return (
    <Box width="100%">
      <Alert status="error" mb="24px" borderRadius="6px">
        <AlertIcon />
        <AlertDescription>{externalMessage}</AlertDescription>
      </Alert>
      {!!data?.length && (
        <>
          <ScrollableTable numOfRows={numOfRows}>
            <Table mb="12px" border="1px" borderColor={colors.gray[100]}>
              <Thead>
                <Tr>
                  {Object.keys(data[0]).map((key) => (
                    <Th key={key}>{key}</Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody fontSize="sm">
                {data.map((row, index) => (
                  // the index is stable, its okay to use it as a key
                  // eslint-disable-next-line react/no-array-index-key
                  <Tr key={index}>
                    {Object.entries(row).map(([key, value]) => (
                      // !TODO @eldarshamukhamedov Remove cast to `any` for `value` / Fix `csvStringToJsonArray` return types
                      <Td key={key}>{value as any}</Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </ScrollableTable>
          <Text fontSize="xs" mb="24px">
            {omittedRows > 0
              ? `${omittedRows} other entries omitted here.`
              : ''}
          </Text>
        </>
      )}
    </Box>
  );
};

// Exports
type DataTapeErrorViewProps = {
  errors: progress.AggregatedError[];
  numOfRows: number;
  collapsible?: boolean;
};
export const DataTapeErrorView = ({
  errors,
  numOfRows,
  collapsible = true,
}: DataTapeErrorViewProps) => {
  return errors.length > 0 ? (
    <>
      {errors.map((error, index) => {
        switch (error.type) {
          case 'message':
            return <MessageErrorView key={uuidv4()} error={error} />;
          case 'dataValidation':
            return (
              <DataValidationErrorView
                // the index is stable, its okay to use it as a key
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                error={error}
                numOfRows={numOfRows}
                collapsible={collapsible}
              />
            );
          case 'structuredPublicError':
            return (
              <StructuredPublicErrorView
                // the index is stable, its okay to use it as a key
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                error={error}
                numOfRows={numOfRows}
              />
            );
          default:
            return <></>;
        }
      })}
    </>
  ) : (
    <Alert status="error" borderRadius="8px">
      <AlertIcon />
      <AlertDescription>
        Error encountered. We are working on giving you more detailed error
        information. Contact your Finley representative for support.
      </AlertDescription>
    </Alert>
  );
};
